/**
 * /research/chopbook — "Chopbook Tool" tool-detail page.
 *
 * Install flow for Chopbook Tool, a standalone Chrome extension that captures
 * company data off PitchBook (`my.pitchbook.com`) and brings it into nu-assist.
 *
 * Deliberately mirrors `BabyChopsV2Tool` (same sections, same CWS-listing
 * install posture, same version widget) — the two are sibling extension lines
 * and a recruiter should not have to learn two different install pages. Where
 * the copy differs, it is because the TOOL differs, not for variety's sake.
 *
 * The three real differences from Baby Chops, each of which is why a paragraph
 * on this page is not a verbatim copy:
 *
 *   1. **Surface.** Chopbook injects on `https://my.pitchbook.com/*`, not
 *      LinkedIn Recruiter. It mounts a Shadow-DOM panel (bottom-right by default) on
 *      every PitchBook page (`packages/extension/src/content/chopbook/index.tsx`).
 *   2. **Auth is federated.** Chopbook has no sign-in of its own: it rides the
 *      NU Assist web-app session cookie (`nu_assist_session`), and its panel
 *      shows a "log in at NU Assist" link when there is no session. That is
 *      why the install steps below say to sign in HERE rather than to sign
 *      into Chrome — Chrome sign-in is irrelevant to whether the panel works.
 *   3. **Both listings are live as of 2026-08-18.** `chopbook-prod` joined the
 *      extension registry (`packages/python-shared/nu_assist_shared/extensions.json`)
 *      when the "Chopbook Tool" listing was published to testers, so
 *      `getCwsListingUrl("prod", "chopbook")` resolves and the no-listing
 *      branch below is no longer stage-reachable — same posture as the Baby
 *      Chops page. Still do not delete it as dead code: `ChopbookTool.test.tsx`
 *      pins it, and it is what the next extension line renders before its
 *      listing exists.
 *
 * Content-only page: the authenticated shell is provided by `WebShellLayout`.
 */
import { Download, Lightbulb } from "lucide-react";
import {
  CHOPBOOK_GUIDE,
  getCwsListingUrl,
  useLatestExtensionVersion,
  useMe,
} from "@nu-assist/shared-fe";
import { NewBadge } from "@nu-assist/ui";
import { resolveStage } from "../../config/featureFlags";
import { announcementFor } from "../../shell/leftMenuModel";
import { ExtensionInstallSection } from "./ExtensionInstallSection";

// Step 1 is NOT redundant with step 3, and dropping it was a real mistake in
// an earlier revision of this page. The reasoning then was "Chrome sign-in is
// irrelevant to whether the panel works" — true of the panel, false of the
// LISTING. The Chopbook listings are Private / Workspace-scoped
// (`chopbook-cws-listing.md`), so the Store only shows the item to a browser
// signed in with the @nusearch.com account it is shared with. Being signed
// into the wrong Chrome profile is the single most common cause of the "item
// not available" page that step 4 then explains — so removing step 1 deleted
// the fix for the error the page goes on to describe.
const INSTALL_STEPS = [
  "Sign into the Chrome Web Store with your @nusearch.com account — the listing is private, so the Store only shows it to that account.",
  "Install ChopBook from its own Chrome Web Store listing.",
  "Sign in to NU Assist in the same browser — ChopBook uses your NU Assist session, it has no separate login.",
  "Chrome keeps it up to date automatically once installed.",
];

/**
 * THE PITCHBOOK COLUMNS CHOPBOOK READS — a note for developers, no longer a
 * section on this page.
 *
 * It was rendered here between 2026-08-20 and 2026-08-31: Jose proposed
 * documenting the columns where a recruiter stands when they are about to run a
 * capture, and Jared accepted (Trello #729 cl.8 item 8a). He then saw it built,
 * on 2026-08-27, and asked for it back out — *"for the users, I guess they
 * probably don't need those that either… they probably don't need that other
 * box either."* Trello #742 cl.5.
 *
 * THE KNOWLEDGE IS NOT THE PAGE, so it stays here rather than going with the
 * markup. What follows is what the section said, and all of it is still true.
 *
 * MATCHING IS BY NAME, NEVER BY POSITION, on both halves of the pipeline: a
 * recruiter who reordered their saved view, or dropped six columns they do not
 * care about, has to put nothing back. Only PRESENCE matters. Unrecognised
 * columns are kept verbatim rather than dropped.
 *
 *   * The grid capture keys each cell on PitchBook's own column identity
 *     (`[data-id="companyWebsite"]` etc. — `pitchbook/scrape.ts`).
 *   * The upload path keys on the header STRING (`_KNOWN_COLS` in
 *     `pitchbook_worker/domain.py`).
 *
 * REQUIRED, and required means required:
 *
 *   * `Companies` — the company name. Always present in PitchBook search results.
 *   * `Website`  — `scrapeCurrentPage` SKIPS any row with no Website value.
 *     That is the whole reason `countUrlSkippedRows` exists, and why the panel
 *     can say "N rows skipped — the Company Website column is not in your
 *     PitchBook view" instead of showing an unexplained empty capture.
 *
 * CAPTURED WHEN PRESENT, none of them blocking a capture: Description, Company
 * Legal Name, Primary PitchBook Industry Group, Primary PitchBook Industry Code,
 * HQ Location, Year Founded, Ownership Status, Valuation Estimate, Last
 * Financing Size, Last Financing Deal Type, Employees, Revenue, LinkedIn URL.
 * Names are PitchBook's, verbatim — they are what the capture matches on.
 *
 * The extension-side pre-capture check (#732) must name the same required pair.
 * If you change one, change the other in the same breath.
 */

// Shared with the extension (panel + popup) so the wording cannot drift.
const { howToSteps: HOW_TO_STEPS, tips: TIPS } = CHOPBOOK_GUIDE;

// Port 5176 = the deletable `@nu-assist-fixtures/pitchbook` server, which
// serves synthetic PitchBook search-results HTML so the panel can be exercised
// without a live PitchBook seat. Mirrors the linkedin-mock shortcut on the
// Baby Chops page; `chopbook-local`'s manifest already whitelists this origin
// (`LOCAL_FIXTURE_PITCHBOOK_MATCH` in `scripts/build-manifest.ts`).
const PITCHBOOK_FIXTURE_BASE = "http://localhost:5176";
const IS_LOCAL = import.meta.env.VITE_APP_STAGE === "local";

export function ChopbookTool() {
  const me = useMe();
  // Same call shape as the Baby Chops page: `resolveStage()` is the WEB app's
  // 2-valued resolver, collapsing `local` → `dev` so a local developer gets
  // the DEV listing rather than a dead link.
  //
  // Resolves on both stages since 2026-08-18, when the prod listing was
  // published to testers and its row landed in the registry. It returned null
  // on PROD before that, which is why the no-listing branch below reads as a
  // first-class state rather than a defensive placeholder.
  const cwsListingUrl = getCwsListingUrl(resolveStage(), "chopbook");
  // Live latest-version read, scoped to the Chopbook listing via the
  // extension-aware endpoint (`?extension=chopbook`). Fails silently to null
  // (listing pre-launch / API down / empty releases.json) → the version line
  // simply hides. On prod that is the expected state until the listing exists.
  const latest = useLatestExtensionVersion({ extension: "chopbook" });
  const availableVersion =
    latest.data?.current.available?.manifest_version ??
    latest.data?.current.coming_soon?.manifest_version ??
    null;

  // Defensive narrow — WebShellLayout's AuthGuard already wraps this.
  if (me.status !== "authenticated") return null;

  return (
    <div data-testid="tool-chopbook" className="p-12">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <nav aria-label="Breadcrumb" className="mb-2 text-caption text-text-secondary">
          Research &amp; Sourcing <span className="px-1">/</span>
          <span className="text-accent">ChopBook</span>
        </nav>
        <div className="flex flex-col gap-5">
          {/* The extension itself, same layout as ChopLink (Jose, 2026-10-08).
              `/chopbook-librarian.png` is the full-size librarian centred on a
              square canvas: the old 128 px icon squeezed him into a square,
              which made him look taller (Lauren). Sized so he reads the same
              size as Sir Chops. */}
          <section
            data-testid="tool-chopbook-hero"
            className="flex flex-wrap items-center gap-6 rounded-md border border-border bg-surface p-5"
          >
            <img
              src="/chopbook-librarian.png"
              alt=""
              aria-hidden
              className="h-44 w-44 shrink-0 rounded-md object-contain"
            />
            <div className="flex min-w-0 flex-1 basis-60 flex-col gap-2">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-h1 text-text">ChopBook</h1>
                {/* `announcementFor` owns the whole rule — released AND
                    in-window (`CHOPBOOK_NEW_BADGE`, from 2026-10-20). */}
                {announcementFor("tool-chopbook") === "new" ? <NewBadge /> : null}
              </div>
              <p className="text-body text-text-secondary">
                Pull company data from PitchBook without copying it out by hand.
              </p>
            </div>
          </section>

          {/* Install — shared with the Baby Chops page. */}
          <ExtensionInstallSection
            extensionName="ChopBook"
            testIdPrefix="tool-chopbook"
            cwsListingUrl={cwsListingUrl}
            availableVersion={availableVersion}
            steps={INSTALL_STEPS}
            // No manual-install warning here (2026-08-14, operator directive).
            // Baby Chops keeps it: its readers came off the Drive ZIP and
            // Load-Unpacked eras, so a stale hand-installed copy is a real
            // population there. Chopbook has only ever been distributed from
            // its CWS listing, so the warning was addressing an audience of
            // roughly the engineers who built it — at the cost of leading the
            // page with an amber alarm. The mechanic it describes is still
            // true (a Load-Unpacked copy has a different extension id and
            // installs alongside the Store one); if hand-installed Chopbook
            // copies ever spread, flip this back rather than re-writing it.
            showManualInstallNotice={false}
            noListingCopy="ChopBook is not available to install on this environment yet — its Chrome Web Store listing is still being created. Nothing to do here for now; this page will show an install button once it exists."
          >
            ChopBook is its own{" "}
            <strong className="font-semibold text-text">
              Chrome extension
            </strong>
            , with its own Chrome Web Store listing — it does not ship inside
            the nu-assist extension or inside ChopLink. Install it directly
            from that listing; Chrome keeps it up to date after that.
          </ExtensionInstallSection>

          {/* How to use */}
          <section className="rounded-md border border-border bg-surface p-5">
            <h2 className="mb-2 text-h3 text-text">How to use</h2>
            <ol className="list-decimal space-y-1 pl-5 text-body text-text-secondary">
              {HOW_TO_STEPS.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <p className="mt-3 text-caption text-text-secondary">
              If the panel asks you to log in, open NU Assist in another tab and
              sign in — ChopBook reads that session rather than keeping one of
              its own.
            </p>
          </section>

          {/* Tips — same card as the ChopLink page. */}
          <section
            data-testid="tool-chopbook-tips"
            className="rounded-md border border-border bg-surface p-5"
          >
            <h2 className="mb-2 flex items-center gap-2 text-h3 text-text">
              <Lightbulb className="h-5 w-5 text-warning-ink" aria-hidden="true" />
              Tips
            </h2>
            <ul className="list-disc space-y-1 pl-5 text-body text-text-secondary">
              {TIPS.map((tip) => (
                <li key={tip}>{tip}</li>
              ))}
            </ul>
          </section>

          {/* Local dev shortcut — only visible when the pitchbook fixture is
              running. Mirrors the linkedin-mock section on the Baby Chops
              page; it is the only way to exercise the capture path without a
              live PitchBook seat. */}
          {IS_LOCAL && (
            <section className="rounded-md border border-dashed border-accent bg-accent-muted p-5">
              <h2 className="mb-1 text-h3 text-text">
                Local dev fixture (pitchbook)
              </h2>
              <p className="mb-3 text-body text-text-secondary">
                Open the synthetic PitchBook search-results page served by the
                local fixture — no PitchBook session required. Run it with{" "}
                <code className="text-text">
                  pnpm --filter @nu-assist-fixtures/pitchbook dev
                </code>
                .
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={`${PITCHBOOK_FIXTURE_BASE}/search-results/s123456/companies`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-label font-bold text-bg hover:bg-accent-hover"
                >
                  <Download size={14} aria-hidden />
                  Open fixture results page
                </a>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
